import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase';

export interface AppliedGutschein {
  code: string;
  value: number;
  originalValue: number;
}

export interface GutscheinApplicationResult {
  success: boolean;
  remainingBalance: number;
  fullyRedeemed: boolean;
  amountDeducted: number;
  error?: string;
}

export async function applyGutscheinToBooking(
  code: string,
  maxAmountToDeduct: number,
  redeemedBy: string
): Promise<GutscheinApplicationResult> {
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/redeem-voucher`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ code, amount: maxAmountToDeduct, redeemedBy }),
    });

    const data = await response.json();

    if (!data.success) {
      return {
        success: false,
        remainingBalance: 0,
        fullyRedeemed: false,
        amountDeducted: 0,
        error: data.error || 'Gutschein nicht gefunden oder bereits eingelöst',
      };
    }

    return {
      success: true,
      remainingBalance: data.remainingBalance,
      fullyRedeemed: data.fullyRedeemed,
      amountDeducted: data.amountDeducted,
    };
  } catch (err) {
    console.error('Error applying Gutschein:', err);
    return {
      success: false,
      remainingBalance: 0,
      fullyRedeemed: false,
      amountDeducted: 0,
      error: 'Unerwarteter Fehler beim Einlösen des Gutscheins',
    };
  }
}

export async function validateMultipleGutscheine(
  gutscheine: AppliedGutschein[]
): Promise<{ success: boolean; error?: string }> {
  for (const gutschein of gutscheine) {
    const { data, error } = await supabase
      .from('Gutschein')
      .select('code, value_eur, is_redeemed')
      .eq('code', gutschein.code)
      .eq('is_redeemed', false)
      .maybeSingle();

    if (error || !data) {
      return {
        success: false,
        error: `Gutschein "${gutschein.code}" nicht gefunden oder bereits eingelöst`,
      };
    }

    if (!data.value_eur || data.value_eur <= 0) {
      return {
        success: false,
        error: `Gutschein "${gutschein.code}" hat kein Guthaben mehr`,
      };
    }
  }

  return { success: true };
}

export async function applyMultipleGutscheine(
  gutscheine: AppliedGutschein[],
  totalAmount: number,
  redeemedBy: string
): Promise<{
  success: boolean;
  finalAmount: number;
  error?: string;
  results: Array<{ code: string; result: GutscheinApplicationResult }>;
}> {
  // Validate all codes first — never deduct anything if any code is invalid
  const validation = await validateMultipleGutscheine(gutscheine);
  if (!validation.success) {
    return {
      success: false,
      finalAmount: totalAmount,
      error: validation.error,
      results: [],
    };
  }

  const results: Array<{ code: string; result: GutscheinApplicationResult }> = [];
  let remainingAmount = totalAmount;

  for (const gutschein of gutscheine) {
    if (remainingAmount <= 0) break;

    const result = await applyGutscheinToBooking(
      gutschein.code,
      remainingAmount,
      redeemedBy
    );

    results.push({ code: gutschein.code, result });

    if (result.success) {
      remainingAmount -= result.amountDeducted;
    } else {
      // A deduction failed mid-way — stop and surface the error
      return {
        success: false,
        finalAmount: totalAmount,
        error: result.error || `Fehler beim Einlösen von Gutschein "${gutschein.code}"`,
        results,
      };
    }
  }

  return {
    success: true,
    finalAmount: Math.max(0, remainingAmount),
    results,
  };
}

export function calculateGutscheinDiscount(
  gutscheine: AppliedGutschein[],
  totalAmount: number
): {
  totalDiscount: number;
  finalAmount: number;
  updatedGutscheine: AppliedGutschein[];
} {
  let remainingAmount = totalAmount;
  let totalDiscount = 0;
  const updatedGutscheine: AppliedGutschein[] = [];

  for (const gutschein of gutscheine) {
    if (remainingAmount <= 0) {
      updatedGutscheine.push({
        ...gutschein,
      });
      continue;
    }

    const amountToDeduct = Math.min(remainingAmount, gutschein.value);
    const newValue = gutschein.value - amountToDeduct;

    updatedGutscheine.push({
      code: gutschein.code,
      value: newValue,
      originalValue: gutschein.originalValue,
    });

    totalDiscount += amountToDeduct;
    remainingAmount -= amountToDeduct;
  }

  return {
    totalDiscount,
    finalAmount: Math.max(0, remainingAmount),
    updatedGutscheine,
  };
}
